/**
 * Acme / shadcn-inspired design tokens.
 * Maps semantic tokens from the reference design to MUI sx values.
 */

export const acme = {
  colors: {
    background: "#ffffff",
    foreground: "#0a0a0a",
    muted: "#f4f4f5",
    mutedForeground: "rgba(113, 113, 122, 0.6)",
    mutedForegroundSolid: "#71717a",
    border: "#e4e4e7",
    primary: "#0a0a0a",
    destructive: "#ef4444",
    destructiveHover: "#dc2626",
    pageBg: "#fafafa",
    /** Soft icon tones — Lucide / shadcn style (not harsh black) */
    iconMuted: "#a1a1aa",
    iconDefault: "#71717a",
    iconEmphasis: "#52525b",
    success: "#22c55e",
    warning: "#f59e0b",
    /** Soft brand green — use instead of legacy #86C232 / #61892F */
    brand: "#22c55e",
    brandHover: "#16a34a",
    brandMuted: "rgba(34, 197, 94, 0.12)",
    brandForeground: "#ffffff",
  },
  radius: {
    sm: "6px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    full: "9999px",
  },
  shadow: {
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)",
  },
  font: {
    sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
};

/** Icon tone map for Lucide dashboard icons */
export const acmeIconTones = {
  muted: acme.colors.iconMuted,
  default: acme.colors.iconDefault,
  foreground: acme.colors.iconEmphasis,
  destructive: acme.colors.destructive,
  success: acme.colors.success,
  brand: acme.colors.brand,
};

/** Neutral heat-map scale for world maps */
export const acmeNeutralColorScale = [
  acme.colors.pageBg,
  acme.colors.muted,
  "#e4e4e7",
  "#d4d4d8",
  acme.colors.iconMuted,
  acme.colors.iconDefault,
  acme.colors.iconEmphasis,
  "#3f3f46",
  acme.colors.foreground,
];

/** Muted chart line colors */
export const acmeChartColors = {
  viewPage: acme.colors.iconDefault,
  registerUsers: acme.colors.iconEmphasis,
  buyPlan: acme.colors.iconMuted,
  projects: "#3f3f46",
  allMoney: acme.colors.foreground,
};

/** Sidebar drawer paper — floating card like MarketingNav */
export const sidebarPaperSx = {
  boxSizing: "border-box",
  backgroundColor: acme.colors.background,
  border: `1px solid ${acme.colors.border}`,
  boxShadow: acme.shadow.xl,
  color: acme.colors.foreground,
  paddingLeft: 1.5,
  paddingRight: 1.5,
  margin: "12px",
  height: "calc(100% - 24px)",
  borderRadius: acme.radius.lg,
  borderRight: `1px solid ${acme.colors.border}`,
  overflow: "hidden",
  paddingTop: 0,
};

/** Nav item button styles */
export const sidebarItemSx = (active, { sub = false, open = false } = {}) => ({
  borderRadius: acme.radius.md,
  mx: 0.25,
  mb: 0.25,
  pl: sub ? 3.5 : 2,
  color:
    active || open ? acme.colors.foreground : acme.colors.mutedForegroundSolid,
  fontWeight: active ? 600 : 400,
  fontSize: "0.875rem",
  backgroundColor: active
    ? acme.colors.muted
    : open
      ? "rgba(0,0,0,0.03)"
      : "transparent",
  transition: "background-color 0.15s, color 0.15s",
  "&:hover": {
    backgroundColor: acme.colors.muted,
    color: acme.colors.foreground,
  },
  "& .MuiListItemText-primary": {
    fontSize: "0.875rem",
  },
});

export const sidebarIconSx = (active, open = false) => ({
  color:
    active || open ? acme.colors.foreground : acme.colors.mutedForegroundSolid,
  minWidth: 36,
  "& .MuiSvgIcon-root": { fontSize: "1.25rem" },
});

export const sidebarSubmenuSx = {
  backgroundColor: "rgba(0,0,0,0.02)",
  borderRadius: acme.radius.md,
  mx: 0.5,
  mb: 0.5,
  overflow: "hidden",
};

/** Primary / outline / ghost button sx maps */
export const acmeButtonSx = {
  primary: {
    borderRadius: acme.radius.md,
    backgroundColor: acme.colors.foreground,
    color: acme.colors.background,
    textTransform: "none",
    fontWeight: 400,
    fontSize: "0.875rem",
    boxShadow: "none",
    px: 2.5,
    py: 1,
    "&:hover": {
      backgroundColor: "rgba(10, 10, 10, 0.9)",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(10, 10, 10, 0.5)",
      color: acme.colors.background,
    },
  },
  primaryPill: {
    borderRadius: acme.radius.full,
    backgroundColor: acme.colors.foreground,
    color: acme.colors.background,
    textTransform: "none",
    fontWeight: 400,
    fontSize: "0.875rem",
    boxShadow: "none",
    px: 2,
    py: 0.75,
    minHeight: 28,
    "&:hover": {
      backgroundColor: "rgba(10, 10, 10, 0.9)",
      boxShadow: "none",
    },
  },
  outline: {
    borderRadius: acme.radius.md,
    border: `1px solid ${acme.colors.border}`,
    color: acme.colors.foreground,
    backgroundColor: "transparent",
    textTransform: "none",
    fontWeight: 400,
    fontSize: "0.875rem",
    boxShadow: "none",
    px: 2.5,
    py: 1,
    "&:hover": {
      backgroundColor: acme.colors.muted,
      border: `1px solid ${acme.colors.border}`,
      boxShadow: "none",
    },
  },
  ghost: {
    color: acme.colors.mutedForegroundSolid,
    textTransform: "none",
    fontWeight: 400,
    fontSize: "0.875rem",
    boxShadow: "none",
    "&:hover": {
      color: acme.colors.foreground,
      backgroundColor: acme.colors.muted,
      boxShadow: "none",
    },
  },
  destructive: {
    borderRadius: acme.radius.md,
    backgroundColor: acme.colors.destructive,
    color: "#fff",
    textTransform: "none",
    fontWeight: 400,
    fontSize: "0.875rem",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: acme.colors.destructiveHover,
      boxShadow: "none",
    },
  },
  secondary: {
    borderRadius: acme.radius.md,
    backgroundColor: acme.colors.muted,
    color: acme.colors.foreground,
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.875rem",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#e4e4e7",
      boxShadow: "none",
    },
  },
  link: {
    color: acme.colors.foreground,
    textTransform: "none",
    fontWeight: 400,
    fontSize: "0.875rem",
    textDecoration: "underline",
    textUnderlineOffset: 4,
    boxShadow: "none",
    minWidth: 0,
    px: 0,
    "&:hover": {
      backgroundColor: "transparent",
      color: "rgba(10, 10, 10, 0.8)",
      boxShadow: "none",
    },
  },
};

/** Button size presets (shadcn) */
export const acmeButtonSizeSx = {
  default: { minHeight: 40, px: 2, py: 1 },
  sm: { minHeight: 36, px: 1.5, py: 0.75, fontSize: "0.8125rem" },
  lg: { minHeight: 44, px: 4, py: 1.25, fontSize: "0.9375rem" },
  icon: { minWidth: 40, minHeight: 40, p: 1 },
};

export const acmeInfoCardSx = {
  p: { xs: 1.25, sm: 1.5 },
  borderRadius: acme.radius.md,
  border: `1px solid ${acme.colors.border}`,
  boxShadow: "none",
  backgroundColor: acme.colors.background,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 0.25,
  position: "relative",
};

/** Active state for Plan / SMS info cards on dashboard */
export const acmeInfoCardActiveSx = (variant = "plan") => {
  const accents = {
    plan: {
      border: "#86efac",
      bg: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
      dot: acme.colors.success,
    },
    sms: {
      border: "#93c5fd",
      bg: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
      dot: "#3b82f6",
    },
  };
  const a = accents[variant] || accents.plan;
  return {
    ...acmeInfoCardSx,
    background: a.bg,
    borderColor: a.border,
    boxShadow: acme.shadow.lg,
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 10,
      bottom: 10,
      width: 3,
      borderRadius: "0 3px 3px 0",
      backgroundColor: a.dot,
    },
  };
};

export const acmeActiveBadgeSx = {
  position: "absolute",
  top: 8,
  right: 8,
  height: 20,
  fontSize: "0.625rem",
  fontWeight: 700,
  letterSpacing: "0.05em",
  backgroundColor: acme.colors.foreground,
  color: acme.colors.background,
  border: "none",
  "& .MuiChip-label": { px: 1 },
};

export const acmeDashboardPanelSx = {
  p: { xs: 1.5, sm: 2 },
  bgcolor: acme.colors.pageBg,
  borderRadius: acme.radius.lg,
  border: `1px solid ${acme.colors.border}`,
};

/** Shared spacing for Emails / SMS / Traffics tab bodies */
export const acmeDashboardTabGapSx = {
  mt: 1.5,
};

export const acmeDashboardStatGridSx = {
  mb: 2,
};

export const acmeDashboardCardContentSx = {
  p: { xs: 2, sm: 3 },
  "&:last-child": { pb: { xs: 2, sm: 3 } },
};

export const acmeDashboardPanelCardSx = {
  height: "100%",
  boxShadow: "none",
};

export const acmeDashboardSectionTitleSx = {
  fontSize: { xs: "1.1rem", sm: "1.25rem" },
  fontWeight: 600,
  color: acme.colors.foreground,
  mb: 0.5,
};

export const acmeDashboardSectionDescSx = {
  mb: { xs: 2, sm: 3 },
  fontSize: "0.875rem",
  color: acme.colors.mutedForegroundSolid,
  lineHeight: 1.5,
};

export const DASHBOARD_STAT_GRID_SPACING = 2;
export const DASHBOARD_CHART_GRID_SPACING = 2;

/** Billing / settings page sections */
export const acmeBillingSectionContentSx = acmeDashboardCardContentSx;

export const acmePageSectionCardSx = {
  mt: 2,
  boxShadow: "none",
};

/** Draft-resume callout on Create Campaign pages */
export const acmeDraftCardSx = {
  mb: 3,
  maxWidth: 720,
  mx: "auto",
  cursor: "pointer",
  border: `1px solid ${acme.colors.brand}`,
  bgcolor: `${acme.colors.brand}08`,
  boxShadow: "none",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: `0 4px 16px ${acme.colors.brand}22`,
  },
};

export const acmePlanCardSx = (selected, disabled = false) => ({
  width: 260,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  textAlign: "center",
  boxShadow: selected ? acme.shadow.lg : "none",
  borderColor: selected ? acme.colors.foreground : acme.colors.border,
  borderWidth: selected ? 2 : 1,
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? "none" : "auto",
  transition: "border-color 0.15s, box-shadow 0.15s",
});

export const acmePlanBadgeSx = {
  backgroundColor: acme.colors.foreground,
  color: acme.colors.background,
  py: 0.5,
  mb: 1,
  borderRadius: acme.radius.sm,
  fontSize: "0.8125rem",
  fontWeight: 600,
};

/** TextField / Select outlined style */
export const acmeTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: acme.radius.sm,
    fontSize: "0.875rem",
    "& fieldset": { borderColor: acme.colors.border },
    "&:hover fieldset": { borderColor: acme.colors.mutedForegroundSolid },
    "&.Mui-focused fieldset": {
      borderColor: acme.colors.foreground,
      borderWidth: 1,
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    color: acme.colors.mutedForegroundSolid,
    "&.Mui-focused": { color: acme.colors.foreground },
  },
};

export const acmeDisabledInputSx = {
  ...acmeTextFieldSx,
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: acme.colors.foreground,
    opacity: 0.85,
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: acme.colors.mutedForegroundSolid,
  },
  "& .MuiOutlinedInput-root.Mui-disabled": {
    backgroundColor: acme.colors.muted,
  },
};

export const acmeInfoCalloutSx = {
  p: 2,
  borderRadius: acme.radius.md,
  border: `1px solid ${acme.colors.border}`,
  backgroundColor: acme.colors.muted,
};

/** Card / table container */
export const acmeCardSx = {
  borderRadius: acme.radius.md,
  border: `1px solid ${acme.colors.border}`,
  boxShadow: acme.shadow.lg,
  backgroundColor: acme.colors.background,
  overflow: "hidden",
};

/** Page title */
/** Page title block — horizontal inset comes from PrivateLayout padding. */
export const acmePageHeaderContentSx = {
  minWidth: 0,
};

export const acmePageTitleSx = {
  fontFamily: acme.font.sans,
  fontWeight: 600,
  letterSpacing: "-0.038em",
  color: acme.colors.foreground,
  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
  lineHeight: 1.1,
  m: 0,
};

export const acmePageSubtitleSx = {
  fontFamily: acme.font.sans,
  color: acme.colors.mutedForegroundSolid,
  fontSize: "0.9375rem",
  letterSpacing: "-0.022em",
  mt: 0.5,
  mb: 0,
};

/** Title inside page cards — smaller than PageHeader, consistent across pages */
export const acmePageBlockTitleSx = {
  fontFamily: acme.font.sans,
  fontWeight: 600,
  fontSize: "1.25rem",
  lineHeight: 1.35,
  letterSpacing: "-0.022em",
  color: acme.colors.foreground,
  m: 0,
};

/** Row for block title + toolbar/actions (Contact Groups pattern) */
export const acmePageBlockHeaderSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  flexWrap: "wrap",
  gap: 1.5,
  mb: 2,
};

/** Dialog paper */
export const acmeDialogPaperSx = {
  borderRadius: acme.radius.lg,
  border: `1px solid ${acme.colors.border}`,
  boxShadow: acme.shadow.xl,
};

/** Table styles */
export const acmeTableSx = {
  "& .MuiTableHead-root .MuiTableCell-root": {
    fontWeight: 600,
    fontSize: "0.8125rem",
    color: acme.colors.foreground,
    backgroundColor: acme.colors.muted,
    borderBottom: `1px solid ${acme.colors.border}`,
    py: 1.5,
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    fontSize: "0.875rem",
    color: acme.colors.foreground,
    borderBottom: `1px solid ${acme.colors.border}`,
    py: 1.5,
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  "& .MuiTableFooter-root .MuiTableCell-root": {
    borderBottom: "none",
    borderTop: `1px solid ${acme.colors.border}`,
  },
};

/** Chip badge */
export const acmeChipSx = {
  borderRadius: acme.radius.full,
  fontSize: "0.75rem",
  height: 24,
  border: `1px solid ${acme.colors.border}`,
  backgroundColor: acme.colors.background,
  color: acme.colors.foreground,
};

/** Semantic status chip tones — text + soft background from acme palette */
export const acmeStatusChipVariants = {
  success: {
    color: acme.colors.success,
    backgroundColor: acme.colors.brandMuted,
    border: "none",
  },
  warning: {
    color: acme.colors.warning,
    backgroundColor: "rgba(245, 158, 11, 0.12)",
    border: "none",
  },
  destructive: {
    color: acme.colors.destructive,
    backgroundColor: "rgba(239, 68, 68, 0.12)",
    border: "none",
  },
  neutral: {
    color: acme.colors.mutedForegroundSolid,
    backgroundColor: acme.colors.muted,
    border: `1px solid ${acme.colors.border}`,
  },
};

export function acmeStatusChipSx(variant = "neutral") {
  return {
    ...acmeChipSx,
    fontWeight: 500,
    ...acmeStatusChipVariants[variant],
  };
}

/** Dropdown menu */
export const acmeMenuPaperSx = {
  borderRadius: acme.radius.md,
  border: `1px solid ${acme.colors.border}`,
  boxShadow: acme.shadow.lg,
  mt: 0.5,
  "& .MuiMenuItem-root": {
    fontSize: "0.875rem",
    py: 1,
    px: 2,
    borderRadius: acme.radius.sm,
    mx: 0.5,
    "&:hover": { backgroundColor: acme.colors.muted },
  },
};

/** AppBar */
export const acmeAppBarSx = (isMobile) => ({
  backgroundColor: isMobile ? acme.colors.foreground : acme.colors.background,
  color: isMobile ? acme.colors.background : acme.colors.foreground,
  boxShadow: "none",
  borderBottom: isMobile ? "none" : `1px solid ${acme.colors.border}`,
});

export const acmeHeaderLinkSx = {
  color: acme.colors.mutedForegroundSolid,
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  whiteSpace: "nowrap",
  borderRadius: acme.radius.sm,
  px: 1.5,
  "&:hover": {
    color: acme.colors.foreground,
    backgroundColor: acme.colors.muted,
  },
};

/** Inter — app-wide UI typography */
export const acmeMarketingFontFamily = acme.font.sans;

export const acmeMarketingPageSx = {
  fontFamily: acmeMarketingFontFamily,
  fontWeight: 400,
  letterSpacing: "-0.022em",
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
};

export const acmeMarketingHeadlineSx = {
  fontFamily: acmeMarketingFontFamily,
  fontWeight: 600,
  letterSpacing: "-0.045em",
  lineHeight: 1.08,
};

export const acmeMarketingSectionTitleSx = {
  fontFamily: acmeMarketingFontFamily,
  fontWeight: 600,
  letterSpacing: "-0.038em",
  lineHeight: 1.12,
};

export const acmeMarketingBodyLeadSx = {
  fontFamily: acmeMarketingFontFamily,
  fontWeight: 400,
  letterSpacing: "-0.022em",
  lineHeight: 1.65,
};

export const acmeMarketingEyebrowSx = {
  fontFamily: acmeMarketingFontFamily,
  fontWeight: 500,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};
